// Megjelenítő komponensek a ds.css osztályaival.
//
// Ugyanazok a nevek és ugyanaz a viselkedés, mint a Flutter oldalon
// (lib/base/ui/ds/) — az átadási leírás kötelező szabályaival együtt:
// státusz és prioritás SOHA nem csak színnel, minden interaktív elem valódi
// gomb, az ikon-gombon kötelező a címke.

import { t } from './i18n';
import { h, icon, icons } from './dom';
import {
  formatDeadline,
  formatDeadlineShort,
  formatElapsed,
  priorityLabel,
  priorityMeta,
  statusLabel,
  statusMeta,
  type TaskPriority,
  type TaskStatus,
  type TaskView,
} from './model';

export const statusIconPath: Record<TaskStatus, string> = {
  todo: 'M8 2.4a5.6 5.6 0 100 11.2 5.6 5.6 0 000-11.2z',
  waitingToAccept: 'M4.6 2.6h6.8M4.6 13.4h6.8M5.4 2.6v3L8 8l2.6-2.5v-3M5.4 13.4v-3L8 8l2.6 2.4v3',
  unaccepted: 'M3.6 3.6l8.8 8.8M12.4 3.6l-8.8 8.8',
  doing: 'M6.4 4.6l5 3.4-5 3.4z',
  completed: 'M4.2 8.4l2.6 2.6 5-5.6',
  failed: 'M8 2.6L14.2 13H1.8zM8 6.6v3M8 11.2v.2',
};

export const priorityIconPath: Record<TaskPriority, string> = {
  low: 'M4.4 6.4L8 10l3.6-3.6',
  mid: 'M4 8h8',
  high: 'M4.4 9.6L8 6l3.6 3.6',
  urgent: 'M8 3v6M8 12.2v.3',
};

/// Státuszjelvény. Ikon ÉS szöveg — a csak-szín változat szándékosan nincs.
export function statusBadge(status: TaskStatus, compact = false): HTMLElement {
  const meta = statusMeta[status];
  const label = statusLabel(status);
  return h(
    'span',
    { class: `badge ${meta.cls}`, title: label },
    h('span', { class: 'ic' }, icon(statusIconPath[status], 12)),
    compact ? null : h('span', { text: label }),
    compact ? h('span', { class: 'sr-only', text: label }) : null,
  );
}

export function priorityTag(priority: TaskPriority): HTMLElement {
  const meta = priorityMeta[priority];
  const label = priorityLabel(priority);
  return h(
    'span',
    { class: `prio ${meta.cls}`, title: label },
    h('span', { class: 'ic' }, icon(priorityIconPath[priority], 12)),
    h('span', { text: label }),
  );
}

/// Monogram a névből — ugyanaz a szabály, mint a Flutter BlazeAvatarban.
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/// Determinisztikus tónus: ugyanaz az ember mindig ugyanolyan színnel.
export function toneOf(name: string): 'a' | 'b' | 'c' | 'd' {
  let hash = 0;
  for (const code of name) hash = (hash * 31 + code.codePointAt(0)!) & 0x7fffffff;
  return (['a', 'b', 'c', 'd'] as const)[hash % 4]!;
}

export function avatar(
  name: string,
  size: 'sm' | 'md' | 'lg' | 'xl' = 'md',
  photoUrl?: string,
): HTMLElement {
  const cls =
    size === 'sm'
      ? 'av av-sm'
      : size === 'lg'
        ? 'av av-lg'
        : size === 'xl'
          ? 'av av-xl'
          : 'av';

  // A `photoUrl` mező a KÖZÖS adatmodellben eddig is ott volt (a mobil írja
  // is), a web viszont sosem mutatta meg — mindenki monogrammal jelent meg.
  // Ha a kép nem tölthető be (törölt fájl, lejárt hivatkozás), VISSZAESÜNK a
  // monogramra: üres kör rosszabb, mint a kezdőbetűk.
  if (photoUrl) {
    const initials = initialsOf(name);
    const image = h('img', {
      src: photoUrl,
      alt: name,
      style: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' },
    });
    const wrap = h(
      'span',
      { class: `${cls} av-${toneOf(name)}`, title: name, style: { overflow: 'hidden' } },
      image,
    );
    image.addEventListener('error', () => {
      wrap.replaceChildren();
      wrap.textContent = initials;
      wrap.style.overflow = '';
    });
    return wrap;
  }

  return h('span', {
    class: `${cls} av-${toneOf(name)}`,
    title: name,
    text: initialsOf(name),
  });
}

export function avatarStack(names: string[], max = 3): HTMLElement {
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  return h(
    'span',
    { class: 'avs', 'aria-label': names.join(', ') },
    ...shown.map((n) => avatar(n, 'sm')),
    rest > 0
      ? h('span', { class: 'av av-sm', text: `+${rest}`, title: t('task.morePeople', [String(rest)]) })
      : null,
  );
}

export function emptyState(options: {
  title: string;
  text?: string;
  actionLabel?: string;
  onAction?: () => void;
}): HTMLElement {
  return h(
    'div',
    { class: 'empty' },
    h('h2', { class: 'h3', text: options.title }),
    options.text ? h('p', { class: 'body-sm ts', text: options.text }) : null,
    options.actionLabel && options.onAction
      ? h('button', {
          class: 'btn btn-primary',
          type: 'button',
          text: options.actionLabel,
          onClick: options.onAction,
        })
      : null,
  );
}

/// Betöltési váz — a tartalom helye nem ugrik meg, amikor megérkeznek az adatok.
export function skeletonList(rows = 5): HTMLElement {
  return h(
    'div',
    { class: 'col g12', style: { padding: '16px' }, 'aria-busy': 'true' },
    ...Array.from({ length: rows }, () =>
      h(
        'div',
        { class: 'card pad16 col g12' },
        h('div', { class: 'row g12' }, h('div', { class: 'skel', style: { width: '90px' } }), h('div', { class: 'skel', style: { width: '70px' } })),
        h('div', { class: 'skel l', style: { width: '60%' } }),
        h('div', { class: 'skel', style: { width: '35%' } }),
      ),
    ),
  );
}

/// Határidő-felirat; lejárt állapotban `.overdue` (piros, félkövér).
///
/// `compact` a terv listasorát adja: naptár-ikon + rövid alak („Ma 16:00").
export function deadlineLabel(task: TaskView, compact = false): HTMLElement | null {
  if (!task.deadline) return null;
  const cls = task.isOverdue ? 'overdue' : 'ts';
  if (!compact) {
    return h('span', { class: `body-sm ${cls}`, text: formatDeadline(task.deadline) });
  }
  return h(
    'span',
    {
      class: `caption ${cls}`,
      title: formatDeadline(task.deadline),
      style: { display: 'inline-flex', alignItems: 'center', gap: '4px' },
    },
    h('span', { class: 'ic', style: { width: '12px', height: '12px' } }, icon(icons.calendar, 12)),
    h('span', { text: formatDeadlineShort(task.deadline) }),
  );
}

/// A feladat mellékjelzései: megjegyzés, csatolmány, eltelt idő.
///
/// `compact` a terv táblázat-oszlopa: 12 px-es ikon + szám, jobbra zárva, 92
/// px-en. A bőbeszédű („4 megjegyzés") változat a mobilkártyán marad, ahol
/// van hely rá — a sorban ugyanez háromszor ennyit foglalna.
export function counters(task: TaskView, compact = false): HTMLElement {
  const parts: HTMLElement[] = [];

  function countWith(path: string, value: number, label: string): HTMLElement {
    return h(
      'span',
      {
        class: 'caption',
        title: label,
        style: { display: 'inline-flex', alignItems: 'center', gap: '4px' },
      },
      h('span', { class: 'ic', style: { width: '12px', height: '12px' } }, icon(path, 12)),
      h('span', { text: String(value) }),
    );
  }

  if (task.commentCount > 0) {
    parts.push(
      compact
        ? countWith(
            icons.comment,
            task.commentCount,
            t('task.commentCount', [String(task.commentCount)]),
          )
        : h('span', {
            class: 'caption',
            text: t('task.commentCount', [String(task.commentCount)]),
          }),
    );
  }
  if (task.attachmentCount > 0) {
    parts.push(
      compact
        ? countWith(
            icons.clip,
            task.attachmentCount,
            t('task.attachmentCount', [String(task.attachmentCount)]),
          )
        : h('span', {
            class: 'caption',
            text: t('task.attachmentCount', [String(task.attachmentCount)]),
          }),
    );
  }
  // A terv (DS04, W05) kis, figyelmeztetes-szinu jelolot mutat: wifi-athuzva
  // ikon, a listaban felirat nelkul. Ez mondja meg, hogy a valtozas latszik
  // ugyan, de meg nem ert fel a szerverre.
  if (task.pending) {
    parts.push(
      h(
        'span',
        { class: 'pending', title: t('task.pendingSave') },
        h('span', { class: 'ic', style: { width: '12px', height: '12px' } }, icon(icons.wifiOff, 12)),
        compact ? null : h('span', { text: t('task.pendingSave') }),
      ),
    );
  }
  if (task.timerRunning) {
    parts.push(
      h(
        'span',
        {
          class: 'caption',
          title: t('task.timerRunning'),
          style: {
            color: 'var(--s-prog)',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          },
        },
        compact
          ? h('span', { class: 'ic', style: { width: '12px', height: '12px' } }, icon(icons.timer, 12))
          : null,
        h('span', {
          text: compact
            ? formatElapsed(task.elapsedSeconds)
            : t('task.measuredPrefix', [formatElapsed(task.elapsedSeconds)]),
        }),
      ),
    );
  } else if (task.elapsedSeconds > 0 && !compact) {
    parts.push(h('span', { class: 'caption', text: formatElapsed(task.elapsedSeconds) }));
  }

  return h(
    'span',
    {
      class: compact ? 'row' : 'row g12 wrap',
      style: compact
        ? { gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }
        : undefined,
    },
    ...parts,
  );
}

/// A varázslók lépéssora (W07, W19).
///
/// A terv számozott köröket és összekötő vonalat mutat: a kész lépés zöld
/// pipa, az aktuális akcentusszínű, a hátralévő halvány. Korábban chipek
/// sorakoztak egymás mellett, amiből nem derült ki, mi van már kész, és a
/// sorrend is csak a helyzetükből látszott.
export function wizardStepper(labels: readonly string[], current: number): HTMLElement {
  const children: (HTMLElement | null)[] = [];

  labels.forEach((label, index) => {
    const done = index < current;
    const active = index === current;

    if (index > 0) {
      children.push(
        h('span', {
          style: {
            flexGrow: '1',
            height: '1px',
            background: 'var(--border)',
            minWidth: '16px',
          },
        }),
      );
    }

    children.push(
      h(
        'div',
        {
          class: 'row g8',
          style: { minWidth: '0', alignItems: 'center' },
          'aria-current': active ? 'step' : undefined,
        },
        h(
          'span',
          {
            class: 'ic',
            style: {
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              fontSize: '12px',
              fontWeight: '700',
              flex: 'none',
              background: done ? 'var(--ok)' : active ? 'var(--accent)' : 'var(--sunken)',
              color: done ? '#fff' : active ? 'var(--on-accent)' : 'var(--tm)',
              border: `1px solid ${done ? 'var(--ok)' : active ? 'var(--accent)' : 'var(--border-strong)'}`,
            },
          },
          done
            ? h('span', { class: 'ic', style: { width: '13px', height: '13px' } }, icon(icons.check, 13))
            : h('span', { text: String(index + 1) }),
        ),
        h('span', {
          class: active ? 'body-sm w6' : 'body-sm ts',
          style: { whiteSpace: 'nowrap' },
          text: label,
        }),
      ),
    );
  });

  return h(
    'div',
    { class: 'row g8', style: { alignItems: 'center' } },
    ...children.filter((c): c is HTMLElement => c !== null),
  );
}

/// A tervek `.cb` jelölőnégyzete címkével.
///
/// A ds.css a natív négyzetet nem stílusozza, a tervekben viszont végig a
/// `.cb` / `.cb.on` páros szerepel. A valódi `<input>` megmarad — csak nem
/// látszik —, hogy a billentyűzet és a képernyőolvasó ugyanúgy kezelje.
export function checkboxRow(options: {
  label: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}): { element: HTMLElement; input: HTMLInputElement } {
  const input = h('input', {
    type: 'checkbox',
    checked: options.checked ? 'checked' : undefined,
    style: {
      position: 'absolute',
      opacity: '0',
      width: '20px',
      height: '20px',
      margin: '0',
      cursor: 'pointer',
    },
  }) as HTMLInputElement;

  const box = h(
    'span',
    { class: `cb${options.checked ? ' on' : ''}`, 'aria-hidden': 'true' },
    options.checked
      ? h('span', { class: 'ic', style: { width: '13px', height: '13px' } }, icon(icons.check, 13))
      : null,
  );

  input.addEventListener('change', () => {
    box.className = `cb${input.checked ? ' on' : ''}`;
    box.replaceChildren(
      input.checked
        ? h('span', { class: 'ic', style: { width: '13px', height: '13px' } }, icon(icons.check, 13))
        : h('span'),
    );
    options.onChange?.(input.checked);
  });

  return {
    input,
    element: h(
      'label',
      {
        class: 'row g10',
        style: { alignItems: 'center', cursor: 'pointer', position: 'relative', gap: '10px' },
      },
      input,
      box,
      h('span', { class: 'body-sm', text: options.label }),
    ),
  };
}

/// „Mostani státusz → új státusz" a tervek szerint (W08, W10).
///
/// Ez mondja meg, MI FOG történni; enélkül a felhasználó csak a választott
/// címkét látta, azt nem, hogy honnan hová kerül a feladat.
export function statusTransition(from: TaskStatus, to: TaskStatus): HTMLElement {
  return h(
    'div',
    { class: 'row g10', style: { alignItems: 'center', flexWrap: 'wrap' } },
    statusBadge(from),
    h(
      'span',
      { class: 'ic tm', style: { width: '14px', height: '14px' }, 'aria-label': t('task.changesTo') },
      icon(icons.arrowRight, 14),
    ),
    statusBadge(to),
  );
}
